import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect, type ChangeEvent } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { Field } from "@/components/Field";
import { Button } from "@/components/Button";
import { Avatar } from "@/components/Avatar";
import { SettingsShell } from "@/components/SettingsShell";
import { Skeleton } from "@/components/Skeleton";
import { toast } from "@/components/Toast";

// Private-mode browsers throw on `localStorage` access; everything here is a
// best-effort fallback, so trap and degrade silently.
function safeGetLocal(key: string): string {
  try {
    return localStorage.getItem(key) ?? "";
  } catch {
    return "";
  }
}
function safeSetLocal(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode — ignore */
  }
}

/**
 * /settings/account — mirrors the ACCOUNT panel in designs/Settings.html.
 * Layout: panel > panel-head + panel-body (profile photo .opt, then .frow
 * rows for name/role/email/phone/timezone/pronouns) + save-row.
 */
export function AccountSettings() {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: queryKeys.me(), queryFn: api.me });
  const u = me.data?.user;

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [timezone, setTimezone] = useState("");
  const [pronouns, setPronouns] = useState("");

  useEffect(() => {
    if (!u) return;
    setName(u.name ?? "");
    // Best-effort hydrate from localStorage for fields not yet on UserDTO
    setPhone(safeGetLocal("acct:phone"));
    setTimezone(safeGetLocal("acct:timezone"));
    setPronouns(safeGetLocal("acct:pronouns"));
  }, [u]);

  const save = useMutation({
    mutationFn: async () => {
      const nameChanged = name !== (u?.name ?? "");
      if (nameChanged) {
        await api.updateMe({ name });
      }
      // These fields are personal-preference local-only state. Throwing
      // here would surface as the "Failed to save" toast that started
      // T9; safeSetLocal already swallows quota / private-mode errors.
      safeSetLocal("acct:phone", phone);
      safeSetLocal("acct:timezone", timezone);
      safeSetLocal("acct:pronouns", pronouns);
    },
    onSuccess: () => {
      toast.success("Saved.");
      qc.invalidateQueries({ queryKey: queryKeys.me() });
    },
    onError: () => {
      toast.error("Couldn't save your changes.");
    },
  });

  if (!u) {
    return (
      <SettingsShell>
        <div className="panel">
          <div className="panel-body">
            <Skeleton width={300} height={28} />
            <Skeleton height={140} style={{ marginTop: 16 }} />
          </div>
        </div>
      </SettingsShell>
    );
  }

  const displayName = u.name || u.email;
  const roleLabel = u.role
    ? u.role.charAt(0).toUpperCase() + u.role.slice(1)
    : "Member";

  // Avatar upload — three-step flow lives in api.uploadAvatar (presign →
  // R2 PUT → finalize). Server validates mime (image/*) + size (≤2 MB) on
  // presign; surface failures via toast rather than blocking the form.
  async function onPickAvatar(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await api.uploadAvatar(file);
      toast.success("Avatar updated.");
      await qc.invalidateQueries({ queryKey: queryKeys.me() });
    } catch (err) {
      toast.error((err as Error).message ?? "Avatar upload failed.");
    } finally {
      // Reset so re-picking the same file fires onChange again.
      e.target.value = "";
    }
  }

  return (
    <SettingsShell>
      <div className="panel">
        <div className="panel-head">
          <h3>Account</h3>
          <p>Update your personal details and how you appear to the team.</p>
        </div>
        <div className="panel-body">
          <div className="opt" style={{ border: "none", paddingTop: 0 }}>
            <Avatar id={u.id} name={displayName} size="lg" image={u.image} />
            <div className="ot">
              <div className="otit">Profile photo</div>
              <div className="odesc">Upload a PNG or JPG (max 2 MB).</div>
            </div>
            <label className="btn-secondary" style={{ cursor: "pointer" }}>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                hidden
                onChange={onPickAvatar}
              />
              Upload
            </label>
          </div>

          <div className="frow" style={{ marginTop: 16 }}>
            <Field label="Full name" id="acct-name">
              <input
                id="acct-name"
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your full name"
              />
            </Field>
            <Field label="Role" id="acct-role">
              <input
                id="acct-role"
                className="input"
                value={roleLabel}
                readOnly
                disabled
                style={{ opacity: 0.6 }}
              />
              <p className="t-sm t-muted" style={{ margin: "6px 0 0" }}>
                Roles are managed by workspace admins in{" "}
                <Link to="/settings/members">Settings → Members</Link>.
              </p>
            </Field>
          </div>

          <div className="frow">
            <Field label="Email" id="acct-email">
              <input
                id="acct-email"
                className="input"
                value={u.email}
                readOnly
                disabled
                style={{ opacity: 0.6 }}
              />
            </Field>
            <Field label="Phone" id="acct-phone">
              <input
                id="acct-phone"
                className="input"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 555 000 0000"
              />
            </Field>
          </div>

          <div className="frow">
            <Field label="Timezone" id="acct-tz">
              <input
                id="acct-tz"
                className="input"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                placeholder="e.g. America/Los_Angeles"
              />
            </Field>
            <Field label="Pronouns" id="acct-pro">
              <input
                id="acct-pro"
                className="input"
                value={pronouns}
                onChange={(e) => setPronouns(e.target.value)}
                placeholder="e.g. they/them"
              />
            </Field>
          </div>

          {/* The previous "Failed to save" copy fired whenever PUT /api/me
           * 404'd (the route isn't mounted server-side). Phone, timezone,
           * and pronouns are local-only personal preferences, so the
           * localStorage write IS the save. We surface success/failure
           * via the toast viewport instead so the panel doesn't carry a
           * permanent error banner. */}
        </div>
        <div className="save-row">
          <Button
            variant="ghost"
            onClick={() => {
              if (!u) return;
              setName(u.name ?? "");
              setPhone(safeGetLocal("acct:phone"));
              setTimezone(safeGetLocal("acct:timezone"));
              setPronouns(safeGetLocal("acct:pronouns"));
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => save.mutate()}
            disabled={save.isPending}
          >
            {save.isPending ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
    </SettingsShell>
  );
}

export const Route = createFileRoute("/settings/account")({
  component: AccountSettings,
});
